/**
 * AI Action Handler Service
 * Handles AI-generated actions and executes them in the frontend
 */

import aiCensusActionsService from './ai-census-actions.service';
import aiDateActionsService from './ai-date-actions.service';
import aiFilterActionsService from './ai-filter-actions.service';
import aiReportActionsService from './ai-report-actions.service';
import aiTransferActionsService from './ai-transfer-actions.service';
import aiDialogActionsService from './ai-dialog-actions.service';
import moment from 'moment';
import logger from '../utilis/logger';

class AIActionHandlerService {
    /**
     * Process AI response and extract actions
     * @param {string} aiResponse - AI response text
     * @returns {Object} Processed response with actions
     */
    processAIResponse(aiResponse) {
        try {
            // Look for action patterns in the AI response
            const actions = this.extractActions(aiResponse);

            return {
                response: aiResponse,
                actions: actions,
                hasActions: actions.length > 0
            };
        } catch (error) {
            logger.error('Error processing AI response:', error);
            return {
                response: aiResponse,
                actions: [],
                hasActions: false,
                error: error.message
            };
        }
    }

    /**
     * Extract actions from AI response
     * @param {string} response - AI response text
     * @returns {Array} Extracted actions
     */
    extractActions(response) {
        const actions = [];
        const lowerResponse = response.toLowerCase();

        // Check for census/percentage actions
        if (lowerResponse.includes('switch to bed capacity') || lowerResponse.includes('bed capacity')) {
            actions.push({
                type: 'census_action',
                action: 'switch_percentage_method',
                method: 'bedCapacity',
                description: 'Switch to Bed Capacity for percentage calculations'
            });
        }

        if (lowerResponse.includes('census average') || lowerResponse.includes('average occupancy')) {
            actions.push({
                type: 'census_action',
                action: 'switch_percentage_method',
                method: 'censusAverage',
                description: 'Switch to Average Occupancy for percentage calculations'
            });
        }

        if (lowerResponse.includes('current census') || lowerResponse.includes('census as of')) {
            actions.push({
                type: 'census_action',
                action: 'switch_percentage_method',
                method: 'censusAsOfNow',
                description: 'Switch to Current Census for percentage calculations'
            });
        }

        if (lowerResponse.includes('add custom') || lowerResponse.includes('custom percentage')) {
            actions.push({
                type: 'census_action',
                action: 'add_custom_percentage',
                description: 'Add custom percentage calculation method'
            });
        }

        return actions;
    }

    /**
     * Execute an action
     * @param {Object} action - Action to execute
     * @returns {Object} Execution result
     */
    async executeAction(action) {
        try {
            switch (action.type) {
                case 'census_action':
                    // Handle census/percentage actions from availableActions
                    return await this.executeCensusAction(action);
                case 'suggest_date_range':
                    return await this.executeDateAction(action);
                case 'date_action':
                    // Handle date actions from availableActions
                    return await this.executeClickedDateAction(action);
                case 'filter_action':
                case 'apply_filter':
                    // Handle filter actions from availableActions
                    return await this.executeFilterAction(action);
                case 'generate_report':
                case 'setup_automated_report':
                    // Handle report actions from availableActions
                    return await this.executeReportAction(action);
                case 'open_automated_report_dialog':
                    // Handle opening automated report dialog with pre-filled data
                    return await this.executeOpenAutomatedReportDialog(action);
                case 'setup_alert':
                case 'open_automated_alert_dialog':
                    // Handle opening automated alert dialog with pre-filled data
                    // setup_alert is the new intent from classifier, open_automated_alert_dialog is the legacy action type
                    return await this.executeOpenAutomatedAlertDialog(action);
                case 'select_frequency':
                    // Handle frequency selection for automated reports
                    return await this.executeSelectFrequency(action);
                case 'use_suggested_name':
                    // Handle using suggested name for automated reports/alerts
                    return await this.executeUseSuggestedName(action);
                case 'custom_name':
                    // Handle custom name input for automated reports/alerts
                    return await this.executeCustomName(action);
                case 'select_condition':
                    // Handle condition selection for automated alerts
                    return await this.executeSelectCondition(action);
                case 'change_transfer_type':
                    // Handle transfer type actions from availableActions
                    return await this.executeTransferAction(action);
                case 'open_chart_dialog':
                    // Handle opening chart dialogs
                    return await this.executeDialogAction(action);
                case 'open_residence_dialog':
                    // Handle opening Residents List dialogs
                    return await this.executeDialogAction(action);
                case 'open_building_dialog':
                    // Handle opening building dialogs
                    return await this.executeDialogAction(action);
                default:
                    return {
                        success: false,
                        error: `Unknown action type: ${action.type}`
                    };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Execute date-related action  
     * @param {Object} action - Date action
     * @returns {Object} Execution result
     */
    async executeDateAction(action) {
        try {
            // DO NOT auto-execute! Return as clickable action for user
            return {
                success: true,
                message: `Click to apply: ${action.label}`,
                action: {
                    type: 'clickable_date_action',
                    label: action.label,
                    startDate: action.startDate,
                    endDate: action.endDate,
                    description: action.description || `Apply ${action.label.toLowerCase()} date range`,
                    method: action.method,
                    buttonText: `Apply ${action.label}`,
                    actionId: `date_${action.method}_${Date.now()}`
                },
                requiresUserConfirmation: true,
                actionType: 'clickable_date_action'
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to process date action: ${error.message}`
            };
        }
    }

    /**
     * Handle user clicking on a date action button
     * This will call the actual DateFilterComponent.handleOnChange function
     * @param {Object} actionData - Date action data from clicked button
     * @returns {Object} Execution result
     */
    async executeClickedDateAction(actionData) {
        try {
            // Get the DateFilterComponent's handleOnChange function
            const dateFilterHandler = aiDateActionsService.getDateFilterHandler();

            logger.log('🔍 Debug date action execution:', {
                actionData,
                hasHandler: !!dateFilterHandler,
                handlerType: typeof dateFilterHandler
            });

            if (!dateFilterHandler) {
                throw new Error('DateFilterComponent handler not available - DateFilterComponent may not be mounted');
            }

            // Convert dates to Date objects and create selection format
            // Handle both old format (startDate/endDate) and new format (dateRange)
            const startDateStr = actionData.startDate || actionData.dateRange?.startDate;
            const endDateStr = actionData.endDate || actionData.dateRange?.endDate;

            if (!startDateStr || !endDateStr) {
                throw new Error('Invalid date action: missing start or end date');
            }

            const startDateObj = moment(startDateStr).startOf('day').toDate();
            const endDateObj = moment(endDateStr).startOf('day').toDate();

            // Create the exact format that DateFilterComponent.handleOnChange expects
            const selection = [{
                startDate: startDateObj,
                endDate: endDateObj,
                key: "selection"
            }];

            logger.log('🎯 Executing clicked date action:', {
                label: actionData.label,
                selection
            });

            // Call the actual DateFilterComponent.handleOnChange function
            await dateFilterHandler(selection);

            return {
                success: true,
                message: `✅ Applied ${actionData.label}`,
                actionPerformed: true,
                actionType: 'date_applied'
            };

        } catch (error) {
            logger.error('❌ Error executing clicked date action:', error);
            return {
                success: false,
                error: `Failed to apply date range: ${error.message}`
            };
        }
    }

    /**
     * Execute census/percentage action (same pattern as date actions)
     * @param {Object} action - Census action
     * @returns {Object} Execution result
     */
    async executeCensusAction(action) {
        try {
            logger.log('📊 Executing census action:', action);

            // Extract the percentage method from the action
            const method = action.method;

            if (!method) {
                throw new Error('Invalid census action: missing method');
            }

            // Get the CensusAverageBox handler (same pattern as date actions)
            const censusHandler = aiCensusActionsService.getCensusHandler();

            if (!censusHandler) {
                throw new Error('CensusAverageBox handler not available - CensusAverageBox may not be mounted');
            }

            // Create a synthetic event with checked=true (to match the Switch component behavior)
            const syntheticEvent = {
                target: { checked: true }
            };

            // Call the actual CensusAverageBox.handleChangePercentage function
            // This is exactly what happens when user clicks the Switch manually
            logger.log('🎯 Calling CensusAverageBox handler with method:', method);
            censusHandler(syntheticEvent, method, false);

            // Get method label for success message
            const methodLabels = {
                'bedCapacity': 'Bed Capacity',
                'censusAverage': 'Average Occupancy',
                'censusAsOfNow': 'Current Census'
            };
            const methodLabel = methodLabels[method] || method;

            return {
                success: true,
                message: `✅ Successfully switched to ${methodLabel} for percentage calculations`,
                actionPerformed: true,
                actionType: 'census_changed',
                newMethod: method,
                methodLabel: methodLabel
            };

        } catch (error) {
            logger.error('❌ Error executing census action:', error);
            return {
                success: false,
                error: `Failed to change percentage method: ${error.message}`
            };
        }
    }

    /**
     * Execute filter-related action
     * @param {Object} action - Filter action
     * @returns {Object} Execution result
     */
    async executeFilterAction(action) {
        try {
            logger.log('🎯 [Action Handler] Executing filter action:', action);
            logger.log('🔍 [Action Handler] Filter action debug:', {
                hasCardKey: !!(action.cardKey || action.cardType),
                hasItemName: !!action.itemName,
                serviceInitialized: aiFilterActionsService.isInitialized(),
                serviceDebugInfo: aiFilterActionsService.getDebugInfo()
            });

            // Call the AI Filter Actions Service to apply the filter
            const cardKey = action.cardKey || action.cardType;
            logger.log('🎯 [Action Handler] Calling applyFilter with:', { cardKey, itemName: action.itemName });

            const result = await aiFilterActionsService.applyFilter(
                cardKey,
                action.itemName
            );

            if (result.success) {
                return {
                    success: true,
                    message: result.message,
                    cardType: result.cardType,
                    itemName: result.itemName,
                    actionPerformed: true,
                    actionType: 'filter_applied',
                    buttonText: `✅ ${result.message}`
                };
            } else {
                return {
                    success: false,
                    error: result.error,
                    availableOptions: result.availableOptions,
                    buttonText: `❌ ${result.error}`
                };
            }

        } catch (error) {
            logger.error('❌ Error executing filter action:', error);
            return {
                success: false,
                error: `Failed to apply filter: ${error.message}`
            };
        }
    }

    /**
     * Execute report action when user clicks on report action button
     * @param {Object} actionData - Report action data from clicked button
     * @returns {Object} Execution result
     */
    async executeReportAction(actionData) {
        try {
            logger.log('🎯 Executing report action:', actionData);
            logger.log('🔍 Action data details:', {
                cardType: actionData.cardType,
                reportType: actionData.reportType,
                isAutomated: actionData.isAutomated,
                frequency: actionData.frequency,
                isCustomCard: actionData.isCustomCard,
                hasCustomCardMetadata: !!actionData.parameters?.customCardMetadata
            });

            // Extract custom card metadata if present
            const customCardMetadata = actionData.customCardMetadata || actionData.parameters?.customCardMetadata;

            if (actionData.isAutomated) {
                // Set up automated report
                const result = await aiReportActionsService.setupAutomatedReport(
                    actionData.cardType,
                    actionData.frequency,
                    actionData.reportType,
                    actionData.parameters?.reportName || `${actionData.cardType}_${actionData.frequency?.toLowerCase()}_report`,
                    {
                        ...actionData.parameters,
                        customCardMetadata: customCardMetadata
                    }
                );

                return {
                    success: true,
                    message: `✅ ${result.message}`,
                    actionPerformed: true,
                    actionType: 'automated_report_setup',
                    result
                };
            } else {
                // Generate immediate report
                if (actionData.reportType === 'excel') {
                    const result = await aiReportActionsService.generateExcelReport(
                        actionData.cardType,
                        actionData.parameters?.customTitle,
                        customCardMetadata
                    );

                    return {
                        success: true,
                        message: `✅ ${result.message}`,
                        actionPerformed: true,
                        actionType: 'excel_report_generated',
                        result
                    };
                } else {
                    const result = await aiReportActionsService.generatePdfReport(
                        actionData.cardType,
                        actionData.parameters?.customTitle || actionData.parameters?.title,
                        customCardMetadata
                    );

                    return {
                        success: true,
                        message: `✅ ${result.message}`,
                        actionPerformed: true,
                        actionType: 'pdf_report_generated',
                        result
                    };
                }
            }

        } catch (error) {
            logger.error('❌ Error executing report action:', error);
            return {
                success: false,
                error: `Failed to execute report action: ${error.message}`
            };
        }
    }

    /**
     * Execute opening automated report dialog with pre-filled data
     * @param {Object} actionData - Action data containing reportData
     * @returns {Object} Execution result
     */
    async executeOpenAutomatedReportDialog(actionData) {
        try {
            logger.log('🎯 Opening automated report dialog:', actionData);

            if (window.reportDialogTrigger && actionData.reportData) {
                window.reportDialogTrigger(actionData.reportData);

                return {
                    success: true,
                    message: `✅ Automated report dialog opened with pre-filled details.`,
                    actionPerformed: true,
                    actionType: 'automated_report_dialog_opened'
                };
            } else {
                return {
                    success: false,
                    message: `❌ Unable to open automated report dialog. Please use the report button directly.`,
                    actionPerformed: false,
                    actionType: 'automated_report_dialog_failed'
                };
            }
        } catch (error) {
            logger.error('❌ Error opening automated report dialog:', error);
            return {
                success: false,
                error: `Failed to open automated report dialog: ${error.message}`
            };
        }
    }

    /**
     * Execute opening automated alert dialog with pre-filled data
     * @param {Object} actionData - Action data containing alertData
     * @returns {Object} Execution result
     */
    async executeOpenAutomatedAlertDialog(actionData) {
        try {
            logger.log('🎯 Opening automated alert dialog:', actionData);

            if (actionData.alertData) {
                // Store the alert data globally for when the dialog becomes available
                window.aiPrefilledAlertData = actionData.alertData;

                // Try to trigger the dialog if the function is available
                if (window.alertDialogTrigger) {
                    window.alertDialogTrigger(actionData.alertData);

                    return {
                        success: true,
                        message: `✅ Automated alert dialog opened with pre-filled details.`,
                        actionPerformed: true,
                        actionType: 'automated_alert_dialog_opened'
                    };
                } else {
                    // If trigger function is not available, store the data and show a message
                    // The dialog will be opened when the ReportButton component mounts
                    logger.log('⚠️ Alert dialog trigger not available yet, storing data for later use');

                    return {
                        success: true,
                        message: `✅ Alert setup data prepared. The alert dialog will open automatically when ready.`,
                        actionPerformed: true,
                        actionType: 'automated_alert_dialog_prepared'
                    };
                }
            } else {
                return {
                    success: false,
                    message: `❌ Unable to open automated alert dialog. Missing alert data.`,
                    actionPerformed: false,
                    actionType: 'automated_alert_dialog_failed'
                };
            }
        } catch (error) {
            logger.error('❌ Error opening automated alert dialog:', error);
            return {
                success: false,
                error: `Failed to open automated alert dialog: ${error.message}`
            };
        }
    }

    /**
     * Execute frequency selection for automated reports
     * @param {Object} actionData - Action data containing frequency value
     * @returns {Object} Execution result
     */
    async executeSelectFrequency(actionData) {
        try {
            logger.log('🎯 Selecting frequency:', actionData);

            // Continue the conversation with the selected frequency value (not label)
            const frequencyMessage = `${actionData.value || actionData.label}`;

            // This will trigger the AI to continue with the next step
            return {
                success: true,
                message: `✅ Selected ${actionData.label} frequency. Continuing setup...`,
                actionPerformed: true,
                actionType: 'frequency_selected',
                continueConversation: frequencyMessage
            };
        } catch (error) {
            logger.error('❌ Error selecting frequency:', error);
            return {
                success: false,
                error: `Failed to select frequency: ${error.message}`
            };
        }
    }

    /**
     * Execute using suggested name
     * @param {Object} actionData - Action data containing suggested name
     * @returns {Object} Execution result
     */
    async executeUseSuggestedName(actionData) {
        try {
            logger.log('🎯 Using suggested name:', actionData);

            // Continue the conversation with the suggested name
            const nameMessage = `Use "${actionData.value}"`;

            return {
                success: true,
                message: `✅ Using suggested name: "${actionData.value}". Continuing setup...`,
                actionPerformed: true,
                actionType: 'name_selected',
                continueConversation: nameMessage
            };
        } catch (error) {
            logger.error('❌ Error using suggested name:', error);
            return {
                success: false,
                error: `Failed to use suggested name: ${error.message}`
            };
        }
    }

    /**
     * Execute custom name input
     * @param {Object} actionData - Action data for custom name
     * @returns {Object} Execution result
     */
    async executeCustomName(actionData) {
        try {
            logger.log('🎯 Custom name input:', actionData);

            return {
                success: true,
                message: `✅ Please type your custom name in the chat.`,
                actionPerformed: true,
                actionType: 'custom_name_requested',
                requiresInput: true
            };
        } catch (error) {
            logger.error('❌ Error requesting custom name:', error);
            return {
                success: false,
                error: `Failed to request custom name: ${error.message}`
            };
        }
    }

    /**
     * Execute condition selection for automated alerts
     * @param {Object} actionData - Action data containing condition value
     * @returns {Object} Execution result
     */
    async executeSelectCondition(actionData) {
        try {
            logger.log('🎯 Selecting condition:', actionData);

            // Continue the conversation with the selected condition
            const conditionMessage = `${actionData.label}`;

            return {
                success: true,
                message: `✅ Selected ${actionData.label} condition. Continuing setup...`,
                actionPerformed: true,
                actionType: 'condition_selected',
                continueConversation: conditionMessage
            };
        } catch (error) {
            logger.error('❌ Error selecting condition:', error);
            return {
                success: false,
                error: `Failed to select condition: ${error.message}`
            };
        }
    }

    /**
     * Execute transfer type action when user clicks on transfer action button
     * @param {Object} actionData - Transfer action data from clicked button
     * @returns {Object} Execution result
     */
    async executeTransferAction(actionData) {
        try {
            logger.log('🎯 Executing transfer action:', actionData);

            const result = await aiTransferActionsService.changeTransferType(
                actionData.parameters?.transferTypeName || actionData.transferType
            );

            return {
                success: true,
                message: `✅ ${result.message}`,
                actionPerformed: true,
                actionType: 'transfer_type_changed',
                result
            };

        } catch (error) {
            logger.error('❌ Error executing transfer action:', error);
            return {
                success: false,
                error: `Failed to execute transfer action: ${error.message}`
            };
        }
    }

    /**
     * Execute dialog action when user clicks on dialog action button
     * @param {Object} actionData - Dialog action data from clicked button
     * @returns {Object} Execution result
     */
    async executeDialogAction(actionData) {
        try {
            logger.log('🎯 Executing dialog action:', actionData);

            let result;

            // CRITICAL: Build options object with custom card metadata if available
            const options = {};

            // Check if this is a custom card action
            if (actionData.customCardMetadata || actionData.parameters?.customCardMetadata) {
                options.customCardMetadata = actionData.customCardMetadata || actionData.parameters?.customCardMetadata;
                logger.log('🎯 Custom card metadata detected:', options.customCardMetadata);
            }

            switch (actionData.type) {
                case 'open_chart_dialog':
                    result = await aiDialogActionsService.openChartDialog(
                        actionData.cardType || actionData.parameters?.cardType,
                        actionData.defaultTab || actionData.parameters?.defaultTab,
                        options
                    );
                    break;

                case 'open_residence_dialog':
                    result = await aiDialogActionsService.openResidenceListDialog(
                        actionData.cardType || actionData.parameters?.cardType,
                        options
                    );
                    break;

                case 'open_building_dialog':
                    result = await aiDialogActionsService.openBuildingDialog({
                        cardType: actionData.cardType || actionData.parameters?.cardType || actionData.parameters?.specificCard,
                        ...actionData.parameters,
                        ...options
                    });
                    break;

                default:
                    throw new Error(`Unknown dialog action type: ${actionData.type}`);
            }

            if (result.success) {
                return {
                    success: true,
                    message: `✅ ${result.message}`,
                    actionPerformed: true,
                    actionType: 'dialog_opened',
                    result
                };
            } else {
                throw new Error(result.error);
            }

        } catch (error) {
            logger.error('❌ Error executing dialog action:', error);
            return {
                success: false,
                error: `Failed to execute dialog action: ${error.message}`
            };
        }
    }

    /**
     * Get available actions for current state
     * @returns {Array} Available actions
     */
    getAvailableActions() {
        const censusSettings = aiCensusActionsService.getCurrentPercentageSettings();
        const actions = [];

        // Add census actions
        censusSettings.availableMethods.forEach(method => {
            if (method.key !== censusSettings.currentMethod) {
                actions.push({
                    type: 'census_action',
                    action: 'switch_percentage_method',
                    method: method.key,
                    description: `Switch to ${method.label}`,
                    currentValue: method.value
                });
            }
        });

        return actions;
    }

    /**
     * Generate action suggestions based on user message
     * @param {string} message - User message
     * @returns {Array} Action suggestions
     */
    generateActionSuggestions(message) {
        const suggestions = [];
        const lowerMessage = message.toLowerCase();

        // Census-related suggestions
        if (lowerMessage.includes('percentage') || lowerMessage.includes('census')) {
            const censusSettings = aiCensusActionsService.getCurrentPercentageSettings();

            suggestions.push({
                type: 'census_action',
                action: 'show_current_settings',
                description: 'Show current percentage calculation settings',
                data: censusSettings
            });

            censusSettings.availableMethods.forEach(method => {
                if (method.key !== censusSettings.currentMethod) {
                    suggestions.push({
                        type: 'census_action',
                        action: 'switch_percentage_method',
                        method: method.key,
                        description: `Switch to ${method.label}`,
                        currentValue: method.value
                    });
                }
            });
        }

        return suggestions;
    }
}

// Create singleton instance
const aiActionHandlerService = new AIActionHandlerService();

export default aiActionHandlerService;
